import type { RouteType } from './types';

/**
 * Route library (GDD section 8). Points are [outside, downfield] in yards relative to the receiver's start:
 * "outside" is toward the receiver's own sideline, so the same route works on either side of the ball.
 * breakIndex is the point where the route turns (0 means it never breaks).
 */
export interface RouteShape {
  points: readonly (readonly [number, number])[];
  breakIndex: number;
}

export const ROUTES: Record<RouteType, RouteShape> = {
  slant: { points: [[0, 0], [0, 2], [-7, 9]], breakIndex: 1 },
  curl: { points: [[0, 0], [0, 12], [0.5, 10]], breakIndex: 1 },
  flat: { points: [[0, 0], [2, 1.5], [8, 2.5]], breakIndex: 1 },
  out: { points: [[0, 0], [0, 10], [7, 10]], breakIndex: 1 },
  go: { points: [[0, 0], [0, 45]], breakIndex: 0 },
  post: { points: [[0, 0], [0, 12], [-9, 30]], breakIndex: 1 },
  corner: { points: [[0, 0], [0, 12], [9, 26]], breakIndex: 1 },
  cross: { points: [[0, 0], [0, 6], [-28, 9]], breakIndex: 1 },
  wheel: { points: [[0, 0], [4, 1], [6, 4], [6, 40]], breakIndex: 2 },
  screen: { points: [[0, 0], [0.5, -2], [1.5, -2]], breakIndex: 1 },
};

/**
 * Builds a route in ball-relative field coordinates (x right, y downfield).
 * depth, when given, moves the break point to that depth and shifts everything after it by the same amount.
 */
export function buildRoute(
  type: RouteType,
  start: { x: number; y: number },
  flip: boolean,
  depth?: number,
): { points: { x: number; y: number }[]; breakIndex: number } {
  const shape = ROUTES[type];
  const startX = flip ? -start.x : start.x;
  // Receivers lined up on the ball count as being on the right.
  const outside = startX < 0 ? -1 : 1;
  const breakPoint = shape.points[shape.breakIndex];
  const shift = depth !== undefined && shape.breakIndex > 0 && breakPoint ? depth - breakPoint[1] : 0;
  const points = shape.points.map(([o, y], i) => ({
    x: startX + o * outside,
    y: start.y + y + (i >= shape.breakIndex && shape.breakIndex > 0 ? shift : 0),
  }));
  return { points, breakIndex: shape.breakIndex };
}
